import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Alert,
    Snackbar
} from '@mui/material';
import {
    Notifications as NotificationsIcon,
    NotificationsOff as NotificationsOffIcon
} from '@mui/icons-material';
import notificationService from '../utils/notificationService';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [permissionStatus, setPermissionStatus] = useState('default');
    const [permissionDialog, setPermissionDialog] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [notifications, setNotifications] = useState([]);

    // Initialize notification service on mount
    useEffect(() => {
        try {
            const supported = notificationService.isNotificationSupported();
            setIsSupported(supported);

            if (supported) {
                setPermissionStatus(notificationService.getPermissionStatus());
            }
        } catch (error) {
            console.warn('Failed to initialize notification service:', error);
            setIsSupported(false);
            setPermissionStatus('denied');
        }
    }, []);    // Show permission dialog for new users or after login
    useEffect(() => {
        if (currentUser && isSupported && permissionStatus === 'default') {
            // Show permission dialog after a short delay
            const timer = setTimeout(() => {
                setPermissionDialog(true);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [currentUser, isSupported, permissionStatus]);

    const requestNotificationPermission = async () => {
        try {
            const result = await notificationService.checkAndRequestPermission();
            setPermissionStatus(notificationService.getPermissionStatus());

            setSnackbar({
                open: true,
                message: result.message,
                severity: result.success ? 'success' : 'warning'
            });

            setPermissionDialog(false);
            return result.success;
        } catch (error) {
            console.error('Failed to request notification permission:', error);
            setSnackbar({
                open: true,
                message: 'Failed to request notification permission',
                severity: 'error'
            });
            return false;
        }
    };

    const sendNotification = (type, data) => {
        if (!currentUser || !currentUser.role) {
            console.warn('No user role available for notification');
            return null;
        }

        try {
            const notification = notificationService.sendRoleBasedNotification(
                currentUser.role,
                type,
                data
            );

            if (notification) {
                // Add to local notifications list for in-app display
                const newNotification = {
                    id: Date.now(),
                    type,
                    data,
                    timestamp: new Date(),
                    read: false
                };

                setNotifications(prev => [newNotification, ...prev.slice(0, 9)]); // Keep last 10
            }

            return notification;
        } catch (error) {
            console.warn('Failed to send notification:', error);
            return null;
        }
    };

    const sendMaintenanceNotification = (type, data) => {
        try {
            return notificationService.sendMaintenanceNotification(type, data);
        } catch (error) {
            console.warn('Failed to send maintenance notification:', error);
            return null;
        }
    }; const markNotificationAsRead = (id) => {
        setNotifications(prev =>
            prev.map(notif =>
                notif.id === id ? { ...notif, read: true } : notif
            )
        );
    };

    const clearAllNotifications = () => {
        setNotifications([]);
    };

    const getUnreadCount = () => {
        return notifications.filter(notif => !notif.read).length;
    };

    const handlePermissionDialogClose = () => {
        setPermissionDialog(false);
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const value = {
        permissionStatus,
        isSupported,
        notifications,
        unreadCount: getUnreadCount(),
        requestNotificationPermission,
        sendNotification,
        sendMaintenanceNotification,
        markNotificationAsRead,
        clearAllNotifications,
        setPermissionDialog
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}

            {/* Permission Request Dialog */}
            <Dialog
                open={permissionDialog}
                onClose={handlePermissionDialogClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsIcon color="primary" />
                        <Typography variant="h6">
                            Enable Notifications
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" paragraph>
                            Stay updated with important maintenance activities!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Enable browser notifications to receive real-time updates about:
                        </Typography>
                        <Box sx={{ ml: 2, mb: 2 }}>
                            <Typography variant="body2" component="li">
                                • New maintenance requests requiring attention
                            </Typography>
                            <Typography variant="body2" component="li">
                                • Task assignments and status updates
                            </Typography>
                            <Typography variant="body2" component="li">
                                • Urgent requests needing immediate action
                            </Typography>
                            <Typography variant="body2" component="li">
                                • Request completions and resolutions
                            </Typography>
                        </Box>

                        {currentUser?.role && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    As a <Chip label={currentUser.role} size="small" color="primary" />,
                                    you'll receive notifications relevant to your role and responsibilities.
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {!isSupported && (
                        <Alert severity="warning">
                            Your browser does not support push notifications.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handlePermissionDialogClose}
                        startIcon={<NotificationsOffIcon />}
                    >
                        Not Now
                    </Button>
                    <Button
                        onClick={requestNotificationPermission}
                        variant="contained"
                        startIcon={<NotificationsIcon />}
                        disabled={!isSupported}
                    >
                        Enable Notifications
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};

export default NotificationContext;
